import { ChangeEventHandler, useEffect, useState } from "react";
import classnames from "classnames";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

import { DispensaryTags } from "@/custom-types/Dispensary";
import { useEventTracker } from "@/hooks/useEventTracker";
import isDispensaryDualLicense from "@/utils/dispensary/isDispensaryDualLicense";

import SearchInput from "@/components/SearchInput";
import MenuTypeSelection from "@/components/Shared/MenuTypeSelection";

type Props = {
  className?: string;
  dispensaryId: number;
  dispensaryTags: DispensaryTags[];
  routeToMenu?: boolean;
};

const SearchBar: React.FC<Props> = ({
  className,
  dispensaryId,
  dispensaryTags,
  routeToMenu = false,
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { push } = useRouter();

  const { publishEvent } = useEventTracker();
  const isDualLicenseDispensary = isDispensaryDualLicense(dispensaryTags);

  const queryParam = searchParams?.get("q");
  const [searchValue, setSearchValue] = useState(queryParam || "");

  useEffect(() => {
    if (!queryParam && searchValue) {
      setSearchValue("");
    }
  }, [queryParam]);

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearchValue(e.target.value);
  };

  const handleClear = () => {
    setSearchValue("");

    const targetSearchParams = new URLSearchParams(searchParams?.toString());
    targetSearchParams.delete("q");
    const queryString = targetSearchParams.toString();

    push(`${pathname}${queryString ? `?${queryString}` : ""}`);
  };

  const pushNewQuery = (newQuery: string) => {
    if (routeToMenu) {
      const targetSearchParams = new URLSearchParams();
      targetSearchParams.set("q", newQuery);
      const menuType = searchParams?.get("menu_type");
      if (menuType) {
        targetSearchParams.set("menu_type", menuType);
      }

      push(`${pathname}/menu?${targetSearchParams.toString()}`);
    } else {
      const targetSearchParams = new URLSearchParams(searchParams?.toString());
      targetSearchParams.set("q", newQuery);
      push(`${pathname}?${targetSearchParams.toString()}`);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  const handleSubmit = (e: any) => {
    // If function was called from onKeyPress, only proceed with Enter
    if (e.key && e.key !== "Enter") {
      return;
    }

    // Remove unwanted characters from search query
    const formattedValue = String(searchValue).replace(/[^\w\s-_]/g, "");

    if (formattedValue !== queryParam) {
      publishEvent({
        action: "Search",
        category: "Dispensary Menu",
        label: formattedValue,
      });
      pushNewQuery(formattedValue);
    }
  };

  const handleMenuChange = (value: string) => {
    const targetSearchParams = new URLSearchParams(searchParams?.toString());
    targetSearchParams.set("menu_type", value);

    push(`${pathname}?${targetSearchParams.toString()}`);
  };

  return (
    <>
      <div className={classnames(className)}>
        <div className="flex">
          <SearchInput
            inputProps={{
              onChange: handleChange,
              onKeyDown: handleSubmit,
              placeholder: "Search menu",
              value: searchValue,
            }}
            clearButtonProps={{
              onClick: handleClear,
            }}
            searchButtonProps={{
              onClick: handleSubmit,
            }}
          />

          <MenuTypeSelection
            dispensaryId={dispensaryId}
            className="ml-sm text-right"
            isDualLicense={isDualLicenseDispensary}
            onMenuTypeChange={handleMenuChange}
            showSelectInput={true}
          />
        </div>
      </div>
    </>
  );
};

export default SearchBar;
